import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Tooltip } from 'components/ui';
import {
  type AtlasGqlTask,
  AtlasGqlDispatchMode,
  useSubscribeToNotificationsForWorkContainerMutation,
  useUnsubscribeFromNotificationsForWorkContainerMutation,
  useWorkContainerForSubscriptionButtonQuery,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';

import { StyledButton } from './NotificationSubscriptions.style';
import {
  getParentCampaignLink,
  getParentWorkOrderLink,
  isParentCampaignSubscribed,
  isParentWorkOrderSubscribed,
} from './utils';

type Props = {
  containerId: string;
  afterSubscribe?: () => void;
};

export const NotificationSubscriptionButton: React.FC<Props> = ({
  containerId,
  afterSubscribe,
}) => {
  const { t } = useTranslation();
  const { data } = useWorkContainerForSubscriptionButtonQuery({
    variables: { id: containerId },
    skip: !containerId,
  });
  const workContainer = data?.task;
  const workContainerTypeName =
    workContainer?.type?.name ?? t('workOrder.workContainer', 'Work Container');
  const workContainerNotificationCopy =
    workContainer?.type?.name === 'Campaign'
      ? t(
          'workOrder.campaignNotificationCopy',
          'You will receive Notifications for new Comments, change in Status, new Attachments, and Work Orders added to this Campaign'
        )
      : workContainer?.type?.name === 'Work Order'
        ? t(
            'workOrder.workOrderNotificationCopy',
            'You will receive Notifications for new Comments, change in Status, new Attachments, and Tasks added to this Work Order'
          )
        : t(
            'workOrder.workContainerNotificationCopy',
            'You will receive Notifications for new Comments, change in Status, and new Attachments'
          );

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe, { loading: subscribeLoading }] =
    useSubscribeToNotificationsForWorkContainerMutation({
      variables: { input: { containerId } },
      update: (cache, { data }) => {
        if (workContainer && data?.subscribeToNotificationsForWorkContainer?.length) {
          cache.modify({
            id: cache.identify(workContainer),
            fields: {
              subscribedToNotifications: () => true,
            },
          });
        }
      },
      onCompleted: () => {
        if (!!afterSubscribe) {
          afterSubscribe();
        }
      },
    });
  const [unsubscribe, { loading: unsubscribeLoading }] =
    useUnsubscribeFromNotificationsForWorkContainerMutation({
      variables: { input: { containerId } },
      update: (cache, { data }) => {
        if (workContainer && data?.unsubscribeFromNotificationsForWorkContainer) {
          cache.modify({
            id: cache.identify(workContainer),
            fields: {
              subscribedToNotifications: () => false,
            },
          });
        }
      },
    });

  const { parentCampaignSubscribed, parentWorkOrderSubscribed, subscribedTooltipCopy } =
    useMemo(() => {
      if (!!workContainer) {
        const parentCampaignSubscribed = isParentCampaignSubscribed(workContainer as AtlasGqlTask);
        const parentWorkOrderSubscribed = isParentWorkOrderSubscribed(
          workContainer as AtlasGqlTask
        );

        const parentType = parentCampaignSubscribed
          ? t('workOrder.campaign', 'Campaign')
          : t('workOrder.workOrder', 'Work Order');
        const parentLink = parentCampaignSubscribed
          ? getParentCampaignLink(workContainer as AtlasGqlTask, false)
          : getParentWorkOrderLink(workContainer as AtlasGqlTask, false);
        const subscribedTooltipCopy =
          parentCampaignSubscribed || parentWorkOrderSubscribed ? (
            <Trans
              i18nKey="workOrder.subscribedViaParentTooltip"
              defaults="You are subscribed to Notifications for the {{parentType}} this {{type}} is in. {{copy}}. Go to the <link/> to Unsubscribe."
              values={{
                parentType,
                type: workContainerTypeName,
                copy: workContainerNotificationCopy,
              }}
              components={{ link: parentLink }}
            />
          ) : (
            t(
              'workOrder.subscribedContainerNotificationsTooltip',
              'You are subscribed to Notifications for this {{type}}. {{copy}}. Click to Unsubscribe.',
              { type: workContainerTypeName, copy: workContainerNotificationCopy }
            )
          );

        return {
          parentCampaignSubscribed,
          parentWorkOrderSubscribed,
          subscribedTooltipCopy,
        };
      }

      return {
        parentCampaignSubscribed: false,
        parentWorkOrderSubscribed: false,
        subscribedTooltipCopy: <></>,
      };
    }, [workContainer, workContainerTypeName, workContainerNotificationCopy, t]);

  if (
    workContainer?.subscribedToNotifications ||
    parentCampaignSubscribed ||
    parentWorkOrderSubscribed
  ) {
    return (
      <Tooltip
        title={
          notificationsEnabled ? (
            subscribedTooltipCopy
          ) : (
            <>
              {t(
                'workOrder.subscribedContainerNotificationsDisabledTooltip',
                "You're subscribed to Notifications for this {{type}} but have Notifications disabled. You can re-enable notifications in",
                { type: workContainerTypeName }
              )}{' '}
              <a href="/settings/notification-settings" target="_blank">
                {t('workOrder.settings', 'settings')}
              </a>
              .
            </>
          )
        }
      >
        <span>
          <StyledButton
            data-testid="work-order-unsubscribe"
            onClick={unsubscribe}
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={unsubscribeLoading}
            disabled={
              !notificationsEnabled || parentCampaignSubscribed || parentWorkOrderSubscribed
            }
          >
            {t('workOrder.subscribed', 'Subscribed')}
          </StyledButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        notificationsEnabled ? (
          t(
            'workOrder.clickToSubscribeContainerTooltip',
            'Click to subscribe to Notifications for this {{type}}. {{copy}}.',
            { type: workContainerTypeName, copy: workContainerNotificationCopy }
          )
        ) : (
          <>
            {t(
              'workOrder.enableWorkContainerNotifications',
              'To subscribe to Notifications, enable Work Container Notifications in'
            )}{' '}
            <a href="/settings/notification-settings" target="_blank">
              {t('workOrder.settings', 'settings')}
            </a>
            .
          </>
        )
      }
    >
      <span>
        <StyledButton
          data-testid="accept-subscription-button"
          style={{ width: '100%' }}
          onClick={subscribe}
          icon={<BellOutlined />}
          loading={subscribeLoading}
          disabled={!notificationsEnabled}
        >
          {t('workOrder.subscribe', 'Subscribe')}
        </StyledButton>
      </span>
    </Tooltip>
  );
};
